import InfiniteScroll from 'react-infinite-scroll-component';
import { css, styled } from 'goober';

import { theme } from '@/theme/setupTheme';
import { useVisibleTableData } from '@/ui/table/helpers/use-visible-data';
import type { TableSkeletonConfig } from '@/ui/table/infinite-table/table-body';
import { TableBody } from '@/ui/table/infinite-table/table-body';
import { TableHeaderCell } from '@/ui/table/infinite-table/table-header-cell';
import type { IHeaderRow, Row, ISort } from '@/ui/table/table.types';
import { useCallback } from 'react';
import type { FetchNextPageOptions } from '@tanstack/react-query';
import type { PrimitiveAtom } from 'jotai';
import { useAtom } from 'jotai';

interface Props<T, Sort extends string | undefined = string> {
  id?: string;
  onSort?: (sort: ISort<Sort>) => void;
  isLoading: boolean;
  data: T[];
  rows: Row[];
  hasMore: boolean;
  onLoadMore?: (options?: FetchNextPageOptions) => Promise<unknown>;
  headers: IHeaderRow<Sort>;
  sortState: PrimitiveAtom<ISort<Sort>>;
  height: string;
  borderColor?: string;
  skeletonConfig?: Partial<TableSkeletonConfig>;
  dataTestId?: string;
  tableShadow?: boolean;
  bodyShadow?: boolean;
  tableBorderRadius?: string;
}

/* Copy-paste component InfiniteScrollTable with Jotai state */
export const InfiniteScrollTableJotai = <
  T,
  Sort extends string | undefined = string,
>({
  id,
  onSort,
  isLoading,
  data,
  rows,
  hasMore,
  onLoadMore,
  headers,
  sortState,
  height,
  borderColor,
  skeletonConfig,
  tableShadow,
  bodyShadow,
  tableBorderRadius,
  dataTestId,
}: Props<T, Sort>) => {
  const [sortData, setSortData] = useAtom(sortState);
  const { visibleColumns, visibleRows } = useVisibleTableData<Sort>({
    rows,
    header: headers,
  });

  const handleSort = (sort: ISort<Sort>) => {
    if (isLoading) return; // prevent multisorting while previously loading
    setSortData(sort);
    onSort?.(sort);
  };

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || !onLoadMore) {
      return;
    }

    await onLoadMore();
  }, [hasMore, onLoadMore]);

  return (
    <InfiniteScroll
      dataLength={data.length}
      next={handleLoadMore}
      hasMore={hasMore}
      loader={null}
      height={height}
      className={ScrollClassName}
    >
      <Table
        id={id}
        data-testid={dataTestId || `${id}-table`}
        shadow={tableShadow}
        radius={tableBorderRadius}
      >
        <thead data-testid={`${id}-thead`}>
          <tr>
            {visibleColumns.map(column => (
              <TableHeaderCell<Sort>
                key={column.columnKey}
                cell={column}
                sort={{ ...sortData, onSort: handleSort }}
              />
            ))}
          </tr>
        </thead>
        <TableBody
          id={id ? `${id}-tbody` : undefined}
          rows={visibleRows}
          isLoading={isLoading}
          borderColor={borderColor}
          skeletonConfig={{
            columns: headers.cells.length,
            rowConfig: headers.cells.map(({ align }) => ({ align })),
            ...skeletonConfig,
          }}
          shadow={bodyShadow}
        />
      </Table>
    </InfiniteScroll>
  );
};

const Table = styled('table')<{ shadow?: boolean; radius?: string }>`
  background-color: ${({ theme }) => theme.colors.basics.white};
  width: calc(100% - 15px);
  position: relative;
  border-radius: 12px;
  box-shadow: ${({ shadow, theme }) => (shadow ? theme.shadow.table : 'none')};
  border-radius: ${({ radius }) => radius ?? '0px'};
`;

const ScrollClassName = css`
  ${theme.mixins.scrollbar}
`;
